/**
 * Where a Bearer client keeps its credentials: the session id and the refresh
 * token (see `./token.ts`).
 *
 * - The app: the OS keychain/keystore. The plugin is imported lazily, so this
 *   module carries no native dependency on the web or in tests.
 * - The Office add-in (KAN-1766): the pane's own localStorage. There is no
 *   keychain in a browser frame, and an httpOnly cookie is what the pane
 *   cannot have: Outlook frames it on its own site, where our cookie is
 *   third-party. The trade-off is that script on our origin can read the
 *   tokens, which an httpOnly cookie would hide. What limits it: the browser
 *   keeps a framed page's storage apart per top-level site, so only the pane
 *   inside Outlook sees these entries, not a tab of 9brains; and logging out
 *   of the pane revokes both tokens at the backend.
 */
import { isOfficePane } from '@/lib/office/host';

export interface CredentialStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

const keychain: CredentialStore = {
  async get(key) {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    const value = await SecureStorage.get(key);
    return typeof value === 'string' && value.length > 0 ? value : null;
  },
  async set(key, value) {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    await SecureStorage.set(key, value);
  },
  async remove(key) {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    await SecureStorage.remove(key);
  },
};

/** Kept apart from everything else the app writes to localStorage. */
const PANE_KEY_PREFIX = '9brains.office.';

const paneStorage: CredentialStore = {
  async get(key) {
    return localStorage.getItem(PANE_KEY_PREFIX + key) || null;
  },
  async set(key, value) {
    localStorage.setItem(PANE_KEY_PREFIX + key, value);
  },
  async remove(key) {
    localStorage.removeItem(PANE_KEY_PREFIX + key);
  },
};

/** The store of the client this document runs in. */
export function credentialStore(): CredentialStore {
  return isOfficePane() ? paneStorage : keychain;
}
